/**
 * 공사현황(work-log) 파일 업로드 정책 — 에이전트 내부 SSOT.
 *
 * 현장보고·작업요청 등 이 에이전트의 사진 첨부 화면이 **모두 이 값을 import**해서 쓴다.
 * 화면마다 상수를 따로 두지 않는다(값이 어긋나는 것을 방지).
 *
 * ⚠️ 실제 차단은 BE(sv-indipia 공통 파일 업로드)가 한다. FE 값은 "사용자 사전 안내"일 뿐이며,
 *    BE 정책과 값이 일치해야 한다. indipiaBE 정책 확인 후 여기에 맞춘다(2026-07-16 결정).
 */

/** 사진 1장당 최대 용량(MB) — 문구·검증이 모두 이 값에서 파생된다. */
const PHOTO_MAX_MB = 4;

/**
 * 허용 이미지 확장자 — 휴대폰 촬영·웹 이미지·PC 편집본을 모두 포괄.
 * jpg/jpeg는 같은 포맷의 두 확장자라 함께 허용한다.
 */
const ACCEPTED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'heif', 'heic', 'webp'] as const;

/** 사진 첨부 정책 (§3.9 현장보고 기준 — 작업요청도 동일 적용) */
export const PHOTO_POLICY = {
  /** 최대 장수 */
  maxCount: 10,
  /** 장당 최대 용량(MB) — 안내 문구 표시용 */
  maxMB: PHOTO_MAX_MB,
  /** 장당 최대 용량(byte) — 검증용 */
  maxBytes: PHOTO_MAX_MB * 1024 * 1024,
  /** <input accept> 값. 모든 이미지 허용(카메라/갤러리 선택 시트 유지 — capture 미지정) */
  accept: 'image/*',
  /** 허용 확장자(소문자) — 안내 문구·검증에 사용 */
  extensions: ACCEPTED_IMAGE_EXTENSIONS,
} as const;

/** 파일명에서 확장자(소문자) 추출 */
function extOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/** 허용 이미지 포맷 여부 — 확장자 기준(HEIC/HEIF는 브라우저별로 MIME이 비어있거나 부정확할 수 있어 확장자로 판별) */
function isAcceptedImageFile(file: File): boolean {
  return (ACCEPTED_IMAGE_EXTENSIONS as readonly string[]).includes(extOf(file.name));
}

/** 보고 내용 최대 글자수 (§3.9) */
export const REPORT_CONTENT_MAX = 1000;

// ─────────────────────────────────────────────────────────────────────────────
// 첨부 사진 유효성 검증 — 공통(현장보고·작업요청 모든 화면이 이 함수만 쓴다).
// 값(PHOTO_POLICY)·선별 로직·안내 문구를 한곳에 모아 화면마다 흩어지지 않게 한다.
// (실제 최종 차단은 BE. 아래는 사용자 사전 안내 + 초과분 제외용.)
// ─────────────────────────────────────────────────────────────────────────────

/** 사진 선별 결과 — 정책 통과분 + 제외 사유별 건수 */
export interface PhotoPickResult {
  /** 정책(포맷·개수·용량) 통과 파일 */
  accepted: File[];
  /** 이미지 포맷이 아니라 제외된 수 */
  formatDropped: number;
  /** 최대 장수 초과로 제외된 수 */
  countDropped: number;
  /** 장당 용량 초과로 제외된 수 */
  sizeDropped: number;
}

/**
 * 파일 목록에서 정책(허용 포맷·최대 장수·장당 용량)에 맞는 것만 선별한다.
 * 포맷 검증을 최우선으로 걸러낸 뒤 장수·용량을 검사한다(BE도 동일 순서로 최종 차단).
 * @param files       새로 추가하려는 파일들
 * @param currentCount 이미 첨부된 장수(남은 여유 계산용)
 */
export function pickValidPhotos(files: File[], currentCount: number): PhotoPickResult {
  const formatOk = files.filter(isAcceptedImageFile);
  const formatDropped = files.length - formatOk.length;

  const room = Math.max(0, PHOTO_POLICY.maxCount - currentCount);
  const withinCount = formatOk.slice(0, room);
  const countDropped = formatOk.length - withinCount.length;
  let sizeDropped = 0;
  const accepted = withinCount.filter((f) => {
    if (f.size > PHOTO_POLICY.maxBytes) {
      sizeDropped += 1;
      return false;
    }
    return true;
  });
  return { accepted, formatDropped, countDropped, sizeDropped };
}

/** 첨부 시점 제외 안내 문구(클라이언트 사전검증). 제외가 없으면 null. */
export function photoDropNotice(r: PhotoPickResult): string | null {
  const msgs: string[] = [];
  if (r.formatDropped > 0) {
    msgs.push(`이미지 파일(${PHOTO_POLICY.extensions.join(', ').toUpperCase()})만 첨부할 수 있어 ${r.formatDropped}개는 제외했습니다.`);
  }
  if (r.countDropped > 0) msgs.push(`최대 ${PHOTO_POLICY.maxCount}장까지 첨부할 수 있어 ${r.countDropped}장은 제외했습니다.`);
  if (r.sizeDropped > 0) msgs.push(`${PHOTO_POLICY.maxMB}MB를 넘는 사진 ${r.sizeDropped}장은 제외했습니다.`);
  return msgs.length ? msgs.join(' ') : null;
}

/**
 * 제출 실패 시 BE 응답(code/status) → 사진 관련 사용자 문구.
 * 사진 개수/용량 초과만 처리하고, 그 외에는 null(호출부가 자체 문구로 처리).
 * BE 계약: `PHOTO_COUNT_EXCEEDED`(개수) / `PAYLOAD_TOO_LARGE`·413(용량).
 */
export function photoSubmitError(code: string | null, status?: number): string | null {
  if (code === 'PHOTO_COUNT_EXCEEDED') {
    return `사진은 최대 ${PHOTO_POLICY.maxCount}장까지 첨부할 수 있습니다. 장수를 줄여 다시 시도해 주세요.`;
  }
  if (status === 413 || code === 'PAYLOAD_TOO_LARGE') {
    return `사진 용량이 너무 큽니다. 장당 ${PHOTO_POLICY.maxMB}MB 이하, 전체 ${PHOTO_POLICY.maxCount}장 기준으로 줄여 첨부해 주세요.`;
  }
  return null;
}
